import { useReducer } from "react";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { MinimalTableHeaderCell } from "src/ui/shared/table/MinimalTableComponents";
import { TableHeader } from "src/ui/shared/table/TableHeader";
import { makeStyles } from "tss-react/mui";

import { useGroupsAndWorkspaceMembers } from "@dashboard/groups/store/helpers";
import { AddMemberDialog } from "@dashboard/shared/components/AddMemberDialog";
import { SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { Paper, Table, TableBody, TableContainer, TableRow } from "@mui/material";
import { PrimaryOutlinedButton } from "@shared/CustomButton";

import { useCurrentWorkspace } from "../api/helpers";
import { Workspace } from "../types";

import { MemberListItem } from "./MemberListItem/MemberListItem";

const useStyles = makeStyles()((theme) => ({
    container: { marginBottom: theme.spacing(3), marginTop: theme.spacing(4) },
    btnsContainer: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingBottom: theme.spacing(5),
    },
}));

type Props = {
    workspace: Workspace;
};
export const MembersTable = (props: Props) => {
    const [dialogOpen, setDialogOpen] = useReducer((state) => !state, false);

    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useStyles();
    const { data: workspace } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();
    const ability = workspaceAbility(workspace);
    const mayManageMember = ability.can("manage", "Member");
    const allMembers = useGroupsAndWorkspaceMembers();

    const getTooltipProps = () => {
        if (!mayManageMember) {
            return { title: t("pages:workspacePage.tooltip.cannotManageMembers"), hide: false };
        }
        return { title: "", hide: true };
    };

    return (
        <SectionContainer>
            <SectionHeader>{t("pages:workspacePage.section.title.membersDetails")}</SectionHeader>
            <TableContainer component={Paper} className={classes.container}>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <MinimalTableHeaderCell align="left"></MinimalTableHeaderCell>
                            <MinimalTableHeaderCell align="left">{t("common:label.email")}</MinimalTableHeaderCell>
                            <MinimalTableHeaderCell align="left">{t("common:label.role")}</MinimalTableHeaderCell>
                            <MinimalTableHeaderCell></MinimalTableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {allMembers.map((member, index) => (
                            <MemberListItem key={member.id} index={index} member={member} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <div className={classes.btnsContainer}>
                <PrimaryOutlinedButton
                    disabled={!mayManageMember}
                    onClick={setDialogOpen}
                    tooltipProps={getTooltipProps()}
                    name="workspaces-add-member"
                >
                    {t("pages:workspacePage.button.addMember")}
                </PrimaryOutlinedButton>
            </div>

            <AddMemberDialog
                id={props.workspace.id}
                name="workspace"
                canAssignOwner={ability.can("manage", "Owner")}
                open={dialogOpen}
                onClose={setDialogOpen}
            />
        </SectionContainer>
    );
};
